/**
 * Tâche 1219 (Eric 2026-06-26) — Écran « Nettoyer les alias ».
 *
 * Les alias de production sont pollués (« marge de crédit » portait l'alias
 * « assurance personnelle ») et ce sont eux qui produisent les fausses
 * détections de doublons : la règle « l'un est déjà un alias de l'autre »
 * rapproche deux termes qui n'ont rien en commun.
 *
 * Choix d'Eric : on DÉSACTIVE, on ne supprime pas. L'alias reste visible dans
 * la fiche, la détection de doublons et les recherches l'ignorent, et un clic
 * suffit pour le réactiver s'il s'avère légitime.
 */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { X, SpinnerGap, ArrowCounterClockwise } from "@phosphor-icons/react";
import { confirmDialog } from "../../lib/uiDialogs";
import { useLang } from "../../contexts/LanguageContext";

const API = process.env.REACT_APP_BACKEND_URL;

const rowKey = (r) => `${r.term_id}::${r.alias}`;

export default function AliasCleanupModal({ open, onClose, onChanged }) {
  const { tr } = useLang();
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState(null);
  const [rows, setRows] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [saving, setSaving] = useState(false);
  const [busyKey, setBusyKey] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/jargon/aliases/suspect`, {
        withCredentials: true,
      });
      setRows(data?.rows || []);
      setStats(data || null);
      setSelected(new Set());
    } catch (e) {
      toast.error(tr("Erreur : ", "Error: ") + (e?.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  const active = rows.filter((r) => !r.disabled);
  const disabled = rows.filter((r) => r.disabled);
  // Tâche 1223 (Eric 2026-06-26) — En production, 49 des 52 alias douteux
  // portent le MÊME motif : « identique au titre du terme ». Les cocher un par
  // un n'apporte aucune décision — c'est le même arbitrage 49 fois.
  const sameAsTitle = active.filter((r) =>
    (r.reasons || []).some(// i18n-donnee: valeur renvoyée par le serveur, comparée telle quelle.
    (why) => why.startsWith("Identique au titre")));

  const disableSameAsTitle = async () => {
    if (sameAsTitle.length === 0) return;
    // Tâche 1232 — On applique désormais la GARDE (1228) à l'existant au lieu
    // de simplement désactiver : depuis 1228 plus aucun alias de ce type n'est
    // créé, donc les retirer remet la base au propre. Rien n'est perdu — ils
    // sont archivés dans `aliases_removed_self`.
    const ok = await confirmDialog(
      tr(
        `Retirer les ${sameAsTitle.length} alias qui répètent le titre de leur terme ?\n\n`
          + "• Ils n'apportent rien : le titre est déjà trouvé par la recherche\n"
          + "• Ce sont eux qui font croire à des doublons entre deux fiches sans rapport\n"
          + "• Rien n'est perdu : chaque alias retiré est archivé sur sa fiche (restaurable)\n"
          + "• Depuis maintenant, ce type d'alias n'est plus créé du tout",
        `Remove the ${sameAsTitle.length} aliases that repeat their own term's title?\n\n`
          + "• They bring nothing: the title is already found by search\n"
          + "• They are what makes two unrelated entries look like duplicates\n"
          + "• Nothing is lost: every removed alias is archived on its entry (restorable)\n"
          + "• From now on, this kind of alias is no longer created at all",
      ),
      { confirmLabel: tr("Retirer", "Remove") },
    );
    if (!ok) return;
    setSaving(true);
    try {
      const { data } = await axios.post(
        `${API}/api/jargon/aliases/apply-guard?dry_run=false`,
        {},
        { withCredentials: true },
      );
      toast.success(tr(
        `${data.removed_total} alias retirés sur ${data.terms_touched} termes. Archivés sur chaque fiche.`,
        `${data.removed_total} aliases removed across ${data.terms_touched} terms. Archived on each entry.`,
      ));
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(tr("Erreur : ", "Error: ") + (e?.response?.data?.detail || e.message));
    } finally {
      setSaving(false);
    }
  };

  const toggle = (r) => {
    setSelected((prev) => {
      const next = new Set(prev);
      const k = rowKey(r);
      if (next.has(k)) next.delete(k);
      else next.add(k);
      return next;
    });
  };

  const toggleAll = () => {
    if (selected.size === active.length) setSelected(new Set());
    else setSelected(new Set(active.map(rowKey)));
  };

  const send = async (path, items, message) => {
    const { data } = await axios.post(
      `${API}/api/jargon/aliases/${path}`,
      { items },
      { withCredentials: true },
    );
    toast.success(message(data));
    await load();
    onChanged?.();
  };

  const disableSelected = async () => {
    const items = active
      .filter((r) => selected.has(rowKey(r)))
      .map((r) => ({ term_id: r.term_id, alias: r.alias }));
    if (items.length === 0) return;
    setSaving(true);
    try {
      await send("disable", items, (d) =>
        `${d.changed} alias désactivé(s). La détection de doublons les ignore désormais — réactivables ici.`);
    } catch (e) {
      toast.error(tr("Erreur : ", "Error: ") + (e?.response?.data?.detail || e.message));
    } finally {
      setSaving(false);
    }
  };

  const enableOne = async (r) => {
    setBusyKey(rowKey(r));
    try {
      await send("enable", [{ term_id: r.term_id, alias: r.alias }], () =>
        `« ${r.alias} » réactivé sur « ${r.term} ».`);
    } catch (e) {
      toast.error(tr("Erreur : ", "Error: ") + (e?.response?.data?.detail || e.message));
    } finally {
      setBusyKey(null);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      data-testid="alias-cleanup-modal"
    >
      <div className="w-full max-w-4xl bg-white rounded-lg shadow-2xl flex flex-col max-h-[88vh]">
        <div className="flex items-start justify-between gap-3 px-4 py-3 border-b border-zinc-200">
          <div className="min-w-0">
            <h2 className="text-base font-semibold text-zinc-900">
              {tr("Nettoyer les alias", "Clean up the aliases")}
            </h2>
            <p className="text-xs text-zinc-500 mt-0.5">
              {tr(
                "Un alias absurde est la cause directe des faux doublons. Deux gestes, et rien ne disparaît sans trace : le bouton en haut à droite retire les alias qui répètent le titre (archivés sur leur fiche, restaurables) ; « Désactiver » neutralise les autres cas — l\u2019alias reste dans la fiche, mais la détection de doublons et les recherches l\u2019ignorent.",
                "A nonsense alias is the direct cause of false duplicates. Two actions, and nothing vanishes without a trace: the button at the top right removes the aliases that repeat their title (archived on their entry, restorable); “Disable” neutralises the other cases — the alias stays on the entry, but duplicate detection and search ignore it.",
              )}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-zinc-400 hover:text-zinc-700 flex-shrink-0"
            data-testid="alias-cleanup-close"
            aria-label={tr("Fermer", "Close")}
          >
            <X size={20} />
          </button>
        </div>

        <div className="px-4 py-2 border-b border-zinc-100 text-xs text-zinc-600 flex flex-wrap items-center gap-x-4 gap-y-2">
          {stats && (
            <span data-testid="alias-cleanup-stats">
              {stats.aliases_total} alias sur {stats.terms_scanned} termes ·{" "}
              <strong className="text-amber-700">{stats.suspect_total} douteux</strong>
              {" · "}
              {stats.disabled_total} déjà désactivé(s)
            </span>
          )}
          {/* Tâches 1223 + 1232 — Le motif « identique au titre » est le même
              arbitrage répété des dizaines de fois : un seul clic suffit. Et
              depuis la garde 1228, aucun nouvel alias de ce type n'est créé :
              les retirer remet donc la base au propre pour de bon. */}
          {sameAsTitle.length > 0 && (
            <button
              onClick={disableSameAsTitle}
              disabled={saving}
              className="ml-auto px-2.5 py-1.5 rounded-lg bg-rose-700 text-white text-xs font-semibold hover:bg-rose-800 disabled:opacity-40"
              data-testid="alias-cleanup-same-as-title-btn"
              title={tr("Retire d'un coup tous les alias qui répètent le titre de leur terme. Chaque alias retiré est archivé sur sa fiche.", "Removes in one go every alias that repeats its own term's title. Each removed alias is archived on its entry.")}
            >
              {saving
                ? tr("Nettoyage…", "Cleaning up…")
                : tr(
                    `Retirer les alias identiques au titre (${sameAsTitle.length})`,
                    `Remove the aliases identical to their title (${sameAsTitle.length})`,
                  )}
            </button>
          )}
        </div>

        <div className="flex-1 overflow-y-auto px-4 py-3">
          {loading ? (
            <div className="flex items-center gap-2 text-sm text-zinc-500 py-8 justify-center">
              <SpinnerGap size={18} className="animate-spin" /> {tr("Analyse des alias…", "Analysing the aliases…")}
            </div>
          ) : rows.length === 0 ? (
            <p className="text-sm text-emerald-700 py-8 text-center" data-testid="alias-cleanup-empty">
              {tr("Aucun alias douteux. Les ", "No suspicious alias. The ")}{stats?.aliases_total ?? 0}
              {tr(" alias du dictionnaire sont tous plausibles : les faux doublons ne viennent donc pas de là.", " aliases in the dictionary are all plausible: the false duplicates do not come from there.")}
            </p>
          ) : (
            <>
              {active.length > 0 && (
                <>
                  <div className="flex items-center gap-2 mb-2">
                    <input
                      type="checkbox"
                      checked={selected.size === active.length && active.length > 0}
                      onChange={toggleAll}
                      data-testid="alias-cleanup-select-all"
                      id="alias-select-all"
                    />
                    <label htmlFor="alias-select-all" className="text-xs text-zinc-600">
                      {tr("Tout sélectionner", "Select all")} ({active.length})
                    </label>
                  </div>
                  <table className="w-full text-sm" data-testid="alias-cleanup-table">
                    <thead>
                      <tr className="text-left text-zinc-500 border-b border-zinc-200">
                        <th className="py-2 pr-2 w-6" />
                        <th className="py-2 pr-3 font-medium">Alias</th>
                        <th className="py-2 pr-3 font-medium">{tr("Terme porteur", "Parent term")}</th>
                        <th className="py-2 font-medium">{tr("Pourquoi c\u2019est douteux", "Why it looks wrong")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {active.map((r) => (
                        <tr
                          key={rowKey(r)}
                          className="border-b border-zinc-100 align-top"
                          data-testid={`alias-row-${r.term_id}`}
                        >
                          <td className="py-2 pr-2">
                            <input
                              type="checkbox"
                              checked={selected.has(rowKey(r))}
                              onChange={() => toggle(r)}
                              data-testid={`alias-check-${r.term_id}`}
                            />
                          </td>
                          <td className="py-2 pr-3 font-mono text-xs text-rose-800 break-words min-w-0">
                            {r.alias}
                          </td>
                          <td className="py-2 pr-3 text-xs text-zinc-800 break-words min-w-0">
                            {r.term}
                          </td>
                          <td className="py-2 text-xs text-zinc-600">
                            <ul className="list-disc pl-4 space-y-0.5">
                              {(r.reasons || []).map((why) => (
                                <li key={why}>{why}</li>
                              ))}
                            </ul>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </>
              )}

              {disabled.length > 0 && (
                <div className="mt-5" data-testid="alias-cleanup-disabled-section">
                  <h3 className="text-xs font-semibold uppercase tracking-wider text-zinc-500 mb-2">
                    Déjà désactivés ({disabled.length})
                  </h3>
                  <ul className="space-y-1">
                    {disabled.map((r) => (
                      <li
                        key={rowKey(r)}
                        className="flex items-center justify-between gap-3 text-xs bg-zinc-50 border border-zinc-200 rounded px-2 py-1.5"
                      >
                        <span className="min-w-0 break-words">
                          <span className="font-mono text-zinc-500 line-through">{r.alias}</span>
                          <span className="text-zinc-400">{tr(" sur ", " on ")}</span>
                          <span className="text-zinc-800">{r.term}</span>
                        </span>
                        <button
                          onClick={() => enableOne(r)}
                          disabled={busyKey === rowKey(r)}
                          className="flex-shrink-0 inline-flex items-center gap-1 px-2 py-1 rounded border border-zinc-300 hover:bg-white disabled:opacity-50"
                          data-testid={`alias-enable-${r.term_id}`}
                        >
                          <ArrowCounterClockwise size={12} />
                          {tr("Réactiver", "Re-enable")}
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 px-4 py-3 border-t border-zinc-200">
          <button
            onClick={onClose}
            className="px-3 py-2 text-sm rounded-lg border border-zinc-300 hover:bg-zinc-50"
            data-testid="alias-cleanup-cancel"
          >
            {tr("Fermer", "Close")}
          </button>
          <button
            onClick={disableSelected}
            disabled={saving || selected.size === 0}
            className="px-3 py-2 text-sm rounded-lg bg-rose-700 text-white hover:bg-rose-800 disabled:opacity-40"
            data-testid="alias-cleanup-disable-btn"
          >
            {saving ? "Enregistrement…" : `Désactiver la sélection (${selected.size})`}
          </button>
        </div>
      </div>
    </div>
  );
}
